import { ReviewModalState } from '@/modules/applications/components/ListApplicationsTable/components/ReviewModal/ReviewModal.types';
import {
  ApplicationStatus,
  ApplicationValidationReviewer,
  ApplicationValidationStatus,
  ApplicationValidationType,
  IApplicationValidation,
  IApplicationWithOrderAndCourse,
} from '@/types/application';
import { UserRole } from '@/types/user';

export const getReviewerPendingValidations = (
  validations: IApplicationValidation[],
  reviewer: ApplicationValidationReviewer,
) => {
  return validations.filter(validation =>
    validation.reviews.some(
      review =>
        review.reviewer === reviewer &&
        review.status === ApplicationValidationStatus.Pending,
    ),
  );
};

export const getValidationNames = (
  applications: IApplicationWithOrderAndCourse[],
  userRoles: UserRole[],
) => {
  const validationNames = applications.reduce((acc, application) => {
    if (userRoles.includes(UserRole.Employer)) {
      getReviewerPendingValidations(
        application.validations || [],
        ApplicationValidationReviewer.Employer,
      ).forEach(validation => acc.add(validation.name));
    }

    if (userRoles.includes(UserRole.TaxBenefitsProvider)) {
      getReviewerPendingValidations(
        application.validations || [],
        ApplicationValidationReviewer.FlexibleRemunerationProvider,
      ).forEach(validation => acc.add(validation.name));
    }

    if (userRoles.includes(UserRole.EducationProvider)) {
      getReviewerPendingValidations(
        application.validations || [],
        ApplicationValidationReviewer.EducationProvider,
      ).forEach(validation => acc.add(validation.name));
    }

    return acc;
  }, new Set<ApplicationValidationType>());

  return Array.from(validationNames);
};

export function formatReviewCalls(
  applications: IApplicationWithOrderAndCourse[],
  reviewWithComments: Partial<ReviewModalState>,
  userRoles: UserRole[],
) {
  const { comments, ...review } = reviewWithComments;
  return applications.flatMap(application => {
    const validations = [
      ...getReviewerPendingValidations(
        application.validations || [],
        ApplicationValidationReviewer.Employer,
      ),
      ...getReviewerPendingValidations(
        application.validations || [],
        ApplicationValidationReviewer.FlexibleRemunerationProvider,
      ),
      ...getReviewerPendingValidations(
        application.validations || [],
        ApplicationValidationReviewer.EducationProvider,
      ),
    ].filter(validation => {
      if (userRoles.includes(UserRole.Employer)) {
        return validation.reviews.some(
          review => review.reviewer === ApplicationValidationReviewer.Employer,
        );
      }

      if (userRoles.includes(UserRole.TaxBenefitsProvider)) {
        return validation.reviews.some(
          review =>
            review.reviewer ===
            ApplicationValidationReviewer.FlexibleRemunerationProvider,
        );
      }

      if (userRoles.includes(UserRole.EducationProvider)) {
        return validation.reviews.some(
          review =>
            review.reviewer === ApplicationValidationReviewer.EducationProvider,
        );
      }

      return false;
    });

    const reviewCalls = [];

    if (
      validations.some(
        validation =>
          validation.name === ApplicationValidationType.FlexibleRemuneration,
      )
    ) {
      reviewCalls.push({
        id: application._id!,
        updatedData: {
          isApproved: review[ApplicationValidationType.FlexibleRemuneration],
          validationType: ApplicationValidationType.FlexibleRemuneration,
          comments,
        },
      });
    }

    if (
      validations.some(
        validation =>
          validation.name === ApplicationValidationType.LearningBudget,
      )
    ) {
      reviewCalls.push({
        id: application._id!,
        updatedData: {
          isApproved: review[ApplicationValidationType.LearningBudget],
          validationType: ApplicationValidationType.LearningBudget,
          comments,
        },
      });
    }

    if (
      validations.some(
        validation => validation.name === ApplicationValidationType.Skills,
      )
    ) {
      reviewCalls.push({
        id: application._id!,
        updatedData: {
          isApproved: review[ApplicationValidationType.Skills],
          validationType: ApplicationValidationType.Skills,
          comments,
        },
      });
    }

    if (
      validations.some(
        validation => validation.name === ApplicationValidationType.Capacity,
      )
    ) {
      reviewCalls.push({
        id: application._id!,
        updatedData: {
          isApproved: review[ApplicationValidationType.Capacity],
          validationType: ApplicationValidationType.Capacity,
          comments,
        },
      });
    }

    if (
      validations.some(
        validation =>
          validation.name === ApplicationValidationType.AdmissionProcess,
      )
    ) {
      reviewCalls.push({
        id: application._id!,
        updatedData: {
          isApproved: review[ApplicationValidationType.AdmissionProcess],
          comments,
          validationType: ApplicationValidationType.AdmissionProcess,
        },
      });
    }

    return reviewCalls;
  });
}

export function showReviewAction(
  application: IApplicationWithOrderAndCourse,
  userRoles: string[],
) {
  const { statuses = [], validations = [] } = application;

  if (!Array.isArray(statuses)) {
    return false;
  }

  if (statuses[statuses.length - 1] !== ApplicationStatus.PendingValidation) {
    return false;
  }

  if (userRoles.includes(UserRole.TaxBenefitsProvider)) {
    const taxBenefitsProviderValidations = getReviewerPendingValidations(
      validations,
      ApplicationValidationReviewer.FlexibleRemunerationProvider,
    );

    return !!taxBenefitsProviderValidations?.length;
  }

  if (userRoles.includes(UserRole.EducationProvider)) {
    const educationProviderValidations = getReviewerPendingValidations(
      validations,
      ApplicationValidationReviewer.EducationProvider,
    );

    return !!educationProviderValidations?.length;
  }

  const employerValidations = getReviewerPendingValidations(
    validations,
    ApplicationValidationReviewer.Employer,
  );
  return !!employerValidations?.length;
}
